// AnaMoni chat surface. Replaces the single-shot NLSearchInput with a
// scrolling thread of user prompts and assistant responses. Multi-turn
// refinement happens automatically — the store's lastFilter is forwarded
// as prior_filter_group on each new turn.
//
// Reusable shape: the props are a thin façade (`onApplyFilter`,
// `composerSlot`) so a future consumer can drop this into another shell
// by swapping the hook + filter sink. The actual chat ↔ planner glue
// lives in `useAnaMoni` + `anaMoniStore`.
import { useEffect, useRef, useState } from "react";
import { ANAMONI } from "../copy/anamoni";
import { useAnaMoni } from "../hooks/useAnaMoni";
import { useAnaMoniStore, type AssistantMessage, type ChatMessage } from "../store/anaMoniStore";
import { useExplorerStore } from "../store/explorerStore";
import { useHotkey } from "../hooks/useHotkey";
import type {
  AnaMoniResponse,
  AnamoniExplanation,
  FilterGroup,
} from "../types/inpact";
import type { AggregationResult } from "../types/chart";
import { countLeafRules, fieldLabel } from "../lib/filterLogic";
import { useCatalogueLabels } from "../hooks/useCatalogueLabels";
import { MessageFeedback } from "./MessageFeedback";
import { Chart, DEFAULT_CUSTOMIZATION } from "./Chart";
import { FilterTreePreview } from "./FilterTreePreview";

export function AnaMoniChat() {
  const { submit } = useAnaMoni();
  const { messages, pending, clear } = useAnaMoniStore();
  const setLastFilter = useAnaMoniStore((s) => s.setLastFilter);
  const setFilterGroup = useExplorerStore((s) => s.setFilterGroup);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState("");
  const [placeholderIdx, setPlaceholderIdx] = useState(0);

  // ⌘K focuses the composer from anywhere.
  useHotkey("mod+k", () => inputRef.current?.focus(), {
    allowInInput: true,
    preventDefault: true,
  });

  // Rotate placeholder every 6s while the thread is empty + idle.
  useEffect(() => {
    if (messages.length > 0 || draft || pending) return;
    const id = window.setInterval(() => {
      setPlaceholderIdx((i) => (i + 1) % ANAMONI.placeholders.length);
    }, 6000);
    return () => window.clearInterval(id);
  }, [messages.length, draft, pending]);

  // Auto-scroll on new message / pending change.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages, pending]);

  const runSubmit = async (q: string) => {
    if (!q.trim()) return;
    setDraft("");
    const result = await submit(q);
    if (
      (result?.kind === "success" || result?.kind === "aggregation")
      && result.autoExecute
    ) {
      setFilterGroup(result.filter);
    }
  };

  const onChipClick = async (refinedQuery: string) => {
    await runSubmit(refinedQuery);
  };

  const onUseAnyway = (resp: Extract<AnaMoniResponse, { kind: "ambiguity" }>) => {
    setFilterGroup(resp.rawFilter);
    setLastFilter(resp.rawFilter);
  };

  const isEmpty = messages.length === 0;

  return (
    <section className="anamoni-chat" aria-label="AnaMoni assistant">
      <header className="anamoni-chat__header">
        <span className="anamoni-chat__brand">
          <span className="anamoni-chat__glyph" aria-hidden="true">{ANAMONI.glyph}</span>
          <span>AnaMoni</span>
        </span>
        {!isEmpty && (
          <button
            type="button"
            className="anamoni-chat__clear"
            onClick={clear}
            title="Clear conversation"
          >
            New chat
          </button>
        )}
      </header>

      <div className="anamoni-chat__thread" ref={scrollRef} aria-live="polite">
        {isEmpty && <EmptyState />}
        {messages.map((m) => (
          <Bubble
            key={m.id}
            message={m}
            onChipClick={onChipClick}
            onUseAnyway={onUseAnyway}
          />
        ))}
        {pending && <ThinkingBubble />}
      </div>

      <form
        className="anamoni-chat__composer"
        onSubmit={(e) => {
          e.preventDefault();
          void runSubmit(draft);
        }}
      >
        <textarea
          ref={inputRef}
          className="anamoni-chat__textarea"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void runSubmit(draft);
            }
          }}
          rows={1}
          placeholder={isEmpty ? ANAMONI.placeholders[placeholderIdx] : "Refine, narrow, or ask something new…"}
          aria-label={ANAMONI.ariaLabel}
          disabled={pending}
        />
        <button
          type="submit"
          className="anamoni-chat__send"
          disabled={pending || !draft.trim()}
          aria-label="Send"
        >
          ↑
        </button>
      </form>
    </section>
  );
}

function EmptyState() {
  return (
    <div className="anamoni-chat__empty">
      <div className="anamoni-chat__empty-glyph" aria-hidden="true">{ANAMONI.glyph}</div>
      <h3>Ask AnaMoni anything about INPACT contacts</h3>
      <p>Try one of these to start:</p>
      <ul>
        {ANAMONI.placeholders.slice(0, 3).map((p) => (
          <li key={p}>"{p}"</li>
        ))}
      </ul>
      <p className="anamoni-chat__empty-hint">
        Then refine with follow-ups like "now only Spain" or "and 1st level certified".
      </p>
    </div>
  );
}

interface BubbleProps {
  message: ChatMessage;
  onChipClick: (refinedQuery: string) => void;
  onUseAnyway: (resp: Extract<AnaMoniResponse, { kind: "ambiguity" }>) => void;
}

function Bubble({ message, onChipClick, onUseAnyway }: BubbleProps) {
  if (message.role === "user") {
    return (
      <div className="anamoni-bubble anamoni-bubble--user">
        <div className="anamoni-bubble__body">{message.text}</div>
      </div>
    );
  }
  const showFeedback = message.response.kind !== "error";
  return (
    <div className="anamoni-bubble anamoni-bubble--assistant">
      <div className="anamoni-bubble__avatar" aria-hidden="true">{ANAMONI.glyph}</div>
      <div className="anamoni-bubble__body">
        <AssistantBody msg={message} onChipClick={onChipClick} onUseAnyway={onUseAnyway} />
        {showFeedback && (
          <MessageFeedback messageId={message.id} />
        )}
      </div>
    </div>
  );
}

function AssistantBody({
  msg,
  onChipClick,
  onUseAnyway,
}: {
  msg: AssistantMessage;
  onChipClick: (refinedQuery: string) => void;
  onUseAnyway: (resp: Extract<AnaMoniResponse, { kind: "ambiguity" }>) => void;
}) {
  const r = msg.response;
  if (r.kind === "error") {
    return (
      <div className="anamoni-bubble__error" role="alert">
        {r.message}
      </div>
    );
  }
  if (r.kind === "out_of_domain") {
    return (
      <>
        <AnswerExplanation explanation={r} />
        <div className="anamoni-bubble__ood">
          <p><strong>{ANAMONI.outOfDomainHeadline}</strong></p>
          <p>{ANAMONI.outOfDomainBody}</p>
        </div>
      </>
    );
  }
  if (r.kind === "refusal") {
    return (
      <div className="anamoni-bubble__refusal">
        <AnswerExplanation explanation={r} />
        <p><strong>Plan not applied.</strong></p>
      </div>
    );
  }
  if (r.kind === "partial" && !r.canApply) {
    return (
      <div className="anamoni-bubble__clarify">
        <AnswerExplanation explanation={r} />
        <p>{r.question ?? "Plan not applicable."}</p>
        {r.chips.length > 0 && (
          <div className="anamoni-bubble__chips">
            {r.chips.map((chip, i) => (
              <button
                key={i}
                type="button"
                className="anamoni-chip"
                onClick={() => onChipClick(chip.refinedQuery)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (r.kind === "aggregation") {
    return <AggregationSummary result={r.result} filter={r.filter} explanation={r} />;
  }
  if (r.kind === "ambiguity") {
    return (
      <div className="anamoni-bubble__clarify">
        <AnswerExplanation explanation={r} />
        <p>{r.question}</p>
        {r.chips.length > 0 && (
          <div className="anamoni-bubble__chips">
            {r.chips.map((chip, i) => (
              <button
                key={i}
                type="button"
                className="anamoni-chip"
                onClick={() => onChipClick(chip.refinedQuery)}
              >
                {chip.label}
              </button>
            ))}
          </div>
        )}
        <button
          type="button"
          className="anamoni-bubble__use-anyway"
          onClick={() => onUseAnyway(r)}
        >
          Use my best guess anyway
        </button>
        <EditInBuilderButton filter={r.rawFilter} label="Use my best guess and edit" />
      </div>
    );
  }
  // success
  return <FilterSummary filter={r.filter} explanation={r} />;
}

function AggregationSummary({
  result, filter, explanation,
}: {
  result: AggregationResult;
  filter: FilterGroup;
  explanation: AnamoniExplanation;
}) {
  const setFilterGroup = useExplorerStore((s) => s.setFilterGroup);
  const setLastFilter = useAnaMoniStore((s) => s.setLastFilter);
  const catalogueLabels = useCatalogueLabels();
  const { chart_spec, rows, total } = result;
  // Use the planner-emitted chart_type as a starting point; the in-chat
  // chart is read-only (no customization panel) — keep it tight at 240px.
  const customization = {
    ...DEFAULT_CUSTOMIZATION,
    chartType: chart_spec.chart_type,
    topN: chart_spec.top_n ?? 10,
    showLegend: true,
  };

  const applyFilter = () => {
    setFilterGroup(filter);
    setLastFilter(filter);
  };
  const onApply = () => applyFilter();
  const onOpenInExplorer = () => {
    applyFilter();
    // Single-page app — scroll the aggregate view into focus so the user
    // lands on the larger, customizable version of the same breakdown.
    requestAnimationFrame(() => {
      const el = document.querySelector(".aggregate-view");
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  return (
    <div className="anamoni-bubble__aggregation">
      <AnswerExplanation explanation={explanation} showAppliedFilters />
      <p>
        <strong>Breakdown by {fieldLabel(chart_spec.group_by, catalogueLabels)}</strong>
        {" "}
        ({total} contacts · {rows.length} groups)
      </p>
      <div className="anamoni-bubble__chart">
        <Chart
          result={result}
          customization={customization}
          height={240}
          exportId={`chat-chart-${chart_spec.group_by}`}
        />
      </div>
      <div className="anamoni-bubble__actions">
        <button
          type="button"
          className="anamoni-chip"
          onClick={onApply}
          title="Apply this breakdown's filter to the explorer"
        >
          Apply as filter
        </button>
        <button
          type="button"
          className="anamoni-chip"
          onClick={onOpenInExplorer}
          title="Apply the filter and open the customizable chart"
        >
          Open in explorer
        </button>
        <EditInBuilderButton filter={filter} />
      </div>
    </div>
  );
}

export function FilterSummary({
  filter,
  explanation,
}: {
  filter: FilterGroup;
  explanation?: AnamoniExplanation;
}) {
  const catalogueLabels = useCatalogueLabels();
  const setFilterGroup = useExplorerStore((state) => state.setFilterGroup);
  const setLastFilter = useAnaMoniStore((state) => state.setLastFilter);
  const hasPolicyExplanation = isCompleteExplanation(explanation);
  const requiresConfirmation = Boolean(
    explanation
    && "autoExecute" in explanation
    && explanation.autoExecute === false,
  );
  const applyFilter = () => {
    setFilterGroup(filter);
    setLastFilter(filter);
  };
  // Deep check: top-level rules can be all-empty subgroups (e.g. a NOT or
  // AND whose only entries were removed), which would otherwise render
  // "Filter applied:" above empty parentheses.
  if (countLeafRules(filter) === 0) {
    return (
      <div>
        {hasPolicyExplanation && <AnswerExplanation explanation={explanation} />}
        <p>
          {requiresConfirmation
            ? "No filters proposed — confirm to show everyone."
            : "No filters — showing everyone."}
        </p>
        {requiresConfirmation && (
          <div className="anamoni-bubble__actions">
            <button type="button" className="anamoni-chip" onClick={applyFilter}>
              Apply proposed filter
            </button>
            <EditInBuilderButton filter={filter} />
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      {hasPolicyExplanation ? (
        <>
          <AnswerExplanation
            explanation={explanation}
            showAppliedFilters={!requiresConfirmation}
          />
          {requiresConfirmation && (
            <div className="anamoni-explanation__filters">
              <p>Proposed filter:</p>
              <FilterTreePreview group={filter} catalogueLabels={catalogueLabels} />
            </div>
          )}
        </>
      ) : (
        <>
          <p>Filter applied:</p>
          <FilterTreePreview group={filter} catalogueLabels={catalogueLabels} />
        </>
      )}
      <div className="anamoni-bubble__actions">
        {requiresConfirmation && (
          <button
            type="button"
            className="anamoni-chip"
            onClick={applyFilter}
          >
            Apply proposed filter
          </button>
        )}
        <EditInBuilderButton filter={filter} />
      </div>
    </div>
  );
}

export function AnswerExplanation({
  explanation,
  showAppliedFilters = false,
}: {
  explanation: AnamoniExplanation;
  showAppliedFilters?: boolean;
}) {
  const catalogueLabels = useCatalogueLabels();
  // Chat history from the previous store schema can survive a deployment.
  // Those messages remain readable, but have no explanation envelope yet.
  if (!isCompleteExplanation(explanation)) return null;
  const applied = explanation.appliedFilterGroup;
  return (
    <div className="anamoni-explanation">
      <p className="anamoni-explanation__interpretation">
        {explanation.interpretation}
      </p>
      <AppliedFilterExplanation
        filter={showAppliedFilters ? applied : null}
        catalogueLabels={catalogueLabels}
      />
      <p className="anamoni-explanation__freshness">
        {freshnessLabel(explanation)}
      </p>
      <LimitationList limitations={explanation.limitations} />
    </div>
  );
}

function isCompleteExplanation(
  explanation: AnamoniExplanation | undefined,
): explanation is AnamoniExplanation {
  return Boolean(
    explanation?.snapshot
    && typeof explanation.interpretation === "string"
    && Array.isArray(explanation.limitations),
  );
}

function AppliedFilterExplanation({
  filter,
  catalogueLabels,
}: {
  filter: FilterGroup | null;
  catalogueLabels: Record<string, string>;
}) {
  if (!filter || countLeafRules(filter) === 0) return null;
  return (
    <div className="anamoni-explanation__filters">
      <p>Applied filters:</p>
      <FilterTreePreview group={filter} catalogueLabels={catalogueLabels} />
    </div>
  );
}

function LimitationList({
  limitations,
}: Pick<AnamoniExplanation, "limitations">) {
  return limitations.map((limitation) => (
    <p
      className="anamoni-explanation__limitation"
      role="status"
      key={`${limitation.code}:${limitation.field ?? ""}`}
    >
      {limitation.message}
    </p>
  ));
}

function freshnessLabel(explanation: AnamoniExplanation): string {
  const population = explanation.snapshot.population;
  const populationLabel = population == null
    ? "INPACT snapshot"
    : `${population} contacts`;
  const age = explanation.snapshot.age_seconds;
  if (age == null) return `${populationLabel} · snapshot time unavailable`;
  if (age < 60) return `${populationLabel} · snapshot less than a minute old`;
  const minutes = Math.floor(age / 60);
  if (minutes < 60) {
    return `${populationLabel} · snapshot ${minutes} minute${minutes === 1 ? "" : "s"} old`;
  }
  const hours = Math.floor(minutes / 60);
  return `${populationLabel} · snapshot ${hours} hour${hours === 1 ? "" : "s"} old`;
}

/** Opens the advanced builder preloaded with a specific answer's filter.
 *
 *  Passes that answer's own filter as the modal's SEED rather than writing it
 *  into the live `filterGroup` first — the store may already hold a newer
 *  filter from a later turn, and committing on click (before the user has
 *  confirmed anything) would silently replace the explorer's live filter,
 *  reset to page 1, and re-run the search, with no way for Cancel to restore
 *  what was there. Seeding instead means: opening never touches the live
 *  filter, and Apply is what commits the edited draft — Cancel leaves the
 *  explorer exactly as it was. The seed is still always THIS answer's own
 *  filter regardless of what's newest in the store, so "edit" on an older
 *  answer edits that answer, not whatever the newest turn left behind. */
function EditInBuilderButton({ filter, label = "Edit in builder" }: {
  filter: FilterGroup;
  label?: string;
}) {
  const openAdvancedFilter = useExplorerStore((s) => s.openAdvancedFilter);
  return (
    <button
      type="button"
      className="anamoni-chip"
      onClick={() => openAdvancedFilter(filter)}
      title="Open this filter in the advanced builder"
    >
      {label}
    </button>
  );
}

function ThinkingBubble() {
  return (
    <div className="anamoni-bubble anamoni-bubble--assistant" aria-busy="true">
      <div className="anamoni-bubble__avatar" aria-hidden="true">{ANAMONI.glyph}</div>
      <div className="anamoni-bubble__body anamoni-bubble__thinking">
        <span className="anamoni-dot"></span>
        <span className="anamoni-dot"></span>
        <span className="anamoni-dot"></span>
        <span className="visually-hidden">{ANAMONI.thinking}</span>
      </div>
    </div>
  );
}
